// src/components/ControlPanel.jsx
import React, { useState, useEffect } from 'react';
import { Upload, RefreshCw, Maximize, RotateCw, Droplet, Zap } from 'lucide-react';
import './ControlPanel.css'; // Importing the CSS file

const ControlPanel = ({ 
    config, 
    setConfig, 
    onUploadBase, 
    onUploadDesign, 
    onDownload, 
    aiLoading, 
    previewBase, 
    previewDesign 
}) => {
    
    // --- LOADING TEXT SIMULATION ---
    const [loadingText, setLoadingText] = useState("Analyzing Fabric...");

    useEffect(() => {
        if (!aiLoading) {
            setLoadingText("Analyzing Fabric..."); // Reset
            return;
        }

        // Phase 1: 0-4s "Analyzing Fabric..."
        // Phase 2: 4-8s "Processing Folds..."
        const timer1 = setTimeout(() => setLoadingText("Processing Folds..."), 4000);
        // Phase 3: 8s+ "Finalizing Realistic Blend..."
        const timer2 = setTimeout(() => setLoadingText("Finalizing Realistic Blend..."), 8000);

        return () => {
            clearTimeout(timer1);
            clearTimeout(timer2);
        };
    }, [aiLoading]);

    // Helper to safely handle changes
    const handleChange = (key, val) => {
        let value = parseFloat(val);
        if (isNaN(value)) value = 0;
        setConfig(prev => ({ ...prev, [key]: value }));
    };

    return (
        <div className="control-panel">
            
            {/* HEADER */}
            <div className="cp-header">
                <h2 className="cp-title">
                    Mockup Editor
                </h2>
            </div>

            {/* SCROLLABLE CONTENT */}
            <div className="cp-content custom-scrollbar">
                
                {/* --- STEP 1: UPLOAD BASE --- */}
                <div className="step-section">
                    <label className="step-label">
                        <span className="step-number">1</span>
                        Base Garment
                    </label>
                    <label className="upload-box">
                        {previewBase ? (
                             <div className="upload-preview">
                                <img src={previewBase} alt="Base" className="preview-thumb base-thumb" />
                                <span className="file-name">File Uploaded</span>
                                <span className="replace-link">Replace</span>
                             </div>
                        ) : (
                            <div className="upload-placeholder">
                                <Upload size={16} className="upload-icon" />
                                <span className="upload-text">Upload Apparel</span>
                            </div>
                        )}
                        <input type="file" className="hidden-input" accept="image/*" onChange={onUploadBase} disabled={aiLoading} />
                    </label>
                </div>

                {/* --- CONNECTOR (Visual Flow) --- */}
                {previewBase && (
                    <div className="step-connector">
                        <div className="connector-line"></div>
                    </div>
                )}

                {/* --- STEP 2: UPLOAD DESIGN --- */}
                <div className="step-section">
                    <label className="step-label">
                        <span className="step-number">2</span>
                        Design Layer
                    </label>
                    <label className="upload-box">
                         {previewDesign ? (
                             <div className="upload-preview">
                                <img src={previewDesign} alt="Design" className="preview-thumb design-thumb" />
                                <span className="file-name">Logo Uploaded</span>
                                <span className="replace-link">Replace</span>
                             </div>
                        ) : (
                            <div className="upload-placeholder">
                                <Upload size={16} className="upload-icon" />
                                <span className="upload-text">Upload Logo</span>
                            </div>
                        )}
                        <input type="file" className="hidden-input" accept="image/*" onChange={onUploadDesign} disabled={aiLoading} />
                    </label>
                </div>

                <div className="divider" />

                {/* --- ADJUSTMENT CONTROLS --- */}
                <div className="controls-section">
                    <label className="section-label">
                        Fine Tune
                    </label>

                    {/* 1. Size Slider */}
                    <div className="control-group">
                        <div className="control-header">
                            <span className="control-name"><Maximize size={10}/> Size</span>
                            <span className="control-value">{(config.scale * 100).toFixed(0)}%</span>
                        </div>
                        <input 
                            type="range" min="0.1" max="1.5" step="0.01" 
                            value={config.scale} 
                            onChange={e => handleChange('scale', e.target.value)} 
                            className="slider-input" 
                        />
                    </div>

                    {/* 2. Rotation Slider */}
                    <div className="control-group">
                        <div className="control-header">
                            <span className="control-name"><RotateCw size={10}/> Rotation</span>
                            <span className="control-value">{config.rotation}°</span>
                        </div>
                        <input 
                            type="range" min="0" max="360" step="1" 
                            value={config.rotation} 
                            onChange={e => handleChange('rotation', e.target.value)} 
                            className="slider-input" 
                        />
                    </div>

                    {/* 3. Opacity Slider */}
                    <div className="control-group">
                        <div className="control-header">
                            <span className="control-name"><Droplet size={10}/> Ink Opacity</span>
                            <span className="control-value">{Math.round((config.opacity || 1) * 100)}%</span>
                        </div>
                        <input 
                            type="range" min="0" max="1" step="0.01" 
                            value={config.opacity !== undefined ? config.opacity : 1} 
                            onChange={e => handleChange('opacity', e.target.value)} 
                            className="slider-input" 
                        />
                    </div>
                </div>
            </div>

            {/* GENERATE BUTTON */}
            <div className="cp-footer">
                <button 
                    onClick={onDownload} 
                    disabled={aiLoading}
                    className={`generate-btn ${aiLoading ? 'loading' : ''}`}
                >
                    {aiLoading ? (
                        <div className="loading-state">
                            <div className="loading-row">
                                <RefreshCw size={14} className="spin-icon" />
                                <span>Generating...</span>
                            </div>
                            <span className="loading-subtext">
                                {loadingText}
                            </span>
                        </div>
                    ) : (
                        <>
                            <Zap size={14} className="zap-icon" />
                            Generate Mockup
                            <span className="credit-badge">1 Credit</span>
                        </>
                    )}
                </button>
            </div>

        </div>
    );
};

export default ControlPanel;